import { computed, inject, provide, type ComputedRef, type InjectionKey } from "vue";
import {
  useMenuNotificationBadgesChangedSubscription,
  useMenuNotificationBadgesQuery,
} from "@/graphql";
import { useAuthorization } from "@/functions/useAuthorization";

function createMenuNotificationBadges(enabled: ComputedRef<boolean>) {
  const authorization = useAuthorization();
  const operationEnabled = computed(() => enabled.value && authorization.ready.value);

  const { result: queryResult } = useMenuNotificationBadgesQuery(() => ({
    enabled: operationEnabled.value,
    fetchPolicy: "network-only",
  }));
  const { result: subscriptionResult } = useMenuNotificationBadgesChangedSubscription(() => ({
    enabled: operationEnabled.value,
  }));

  const counts = computed(() => {
    const badges =
      subscriptionResult.value?.MenuNotificationBadgesChanged ??
      queryResult.value?.MenuNotificationBadges ??
      [];
    return new Map(badges.map((badge) => [badge.menuKey, badge.count]));
  });

  return { counts };
}

type MenuNotificationBadgeState = ReturnType<typeof createMenuNotificationBadges>;
const MenuNotificationBadgeKey = Symbol(
  "MenuNotificationBadges",
) as InjectionKey<MenuNotificationBadgeState>;

export function provideMenuNotificationBadges(enabled: ComputedRef<boolean>) {
  const state = createMenuNotificationBadges(enabled);
  provide(MenuNotificationBadgeKey, state);
  return state;
}

export function useMenuNotificationBadges(enabled: ComputedRef<boolean>) {
  return inject(MenuNotificationBadgeKey) ?? createMenuNotificationBadges(enabled);
}
